import * as React from 'react';

import {
  Alert,
  Autocomplete,
  Box,
  Card,
  CardContent,
  CardHeader,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { fetchDailyPulseByPlacement } from '@src/api';
import { DateRangePicker, DollarFormatter, Page, PercentageFormatter } from '@src/components';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';
import { formatDateToYMD } from '@src/utils/dateUtils';

// Canonical {{placement}}-macro values (see apps/app1/services/placement_mapping.py).
// Anything not listed is prettified from its snake_case name.
const PLACEMENT_LABELS = {
  facebook_mobile_feed: 'Facebook Feed (mobile)',
  facebook_desktop_feed: 'Facebook Feed (desktop)',
  facebook_mobile_reels: 'Facebook Reels',
  facebook_stories: 'Facebook Stories',
  facebook_instream_video: 'Facebook In-stream Video',
  facebook_marketplace: 'Facebook Marketplace',
  facebook_right_column: 'Facebook Right Column',
  facebook_search: 'Facebook Search',
  facebook_video_feeds: 'Facebook Video Feeds',
  facebook_profile_feed: 'Facebook Profile Feed',
  facebook_notification: 'Facebook Notifications',
  instagram_feed: 'Instagram Feed',
  instagram_reels: 'Instagram Reels',
  instagram_stories: 'Instagram Stories',
  instagram_explore: 'Instagram Explore',
  instagram_explore_home: 'Instagram Explore Home',
  instagram_profile_feed: 'Instagram Profile Feed',
  instagram_search: 'Instagram Search',
  an: 'Audience Network',
  messenger_inbox: 'Messenger Inbox',
  messenger_stories: 'Messenger Stories',
  others: 'Other',
  unknown: 'Unattributed (no placement on lead)',
};
const UNKNOWN = 'unknown';

const prettify = (value) =>
  String(value || '')
    .split('_')
    .filter(Boolean)
    .map((token) => token.charAt(0).toUpperCase() + token.slice(1))
    .join(' ');

const placementLabel = (value) => PLACEMENT_LABELS[value] || prettify(value) || 'Unknown';

const safeNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const safeDiv = (numerator, denominator) => {
  const num = safeNumber(numerator);
  const denom = safeNumber(denominator);
  return denom ? num / denom : 0;
};

const getCurrentMonthRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  end.setHours(23, 59, 59, 999);
  return { start, end };
};

function aggregatePlacementRows(summaryRows = [], selectedClientId = '', selectedVertical = '') {
  const buckets = new Map();

  (summaryRows || []).forEach((row = {}) => {
    const clientId = row.client_id == null ? '' : String(row.client_id);
    if (selectedClientId && clientId !== String(selectedClientId)) {
      return;
    }
    const vertical = (row.vertical || row.vertical_label || '').trim();
    if (selectedVertical && vertical !== selectedVertical) {
      return;
    }
    const placement = String(row.placement || UNKNOWN).toLowerCase();

    if (!buckets.has(placement)) {
      buckets.set(placement, {
        placement,
        label: placementLabel(placement),
        total_spend_sum: 0,
        total_leads_sum: 0,
        booked_sum: 0,
        scheduled_sum: 0,
        issues_sum: 0,
        demos_sum: 0,
        gross_sales_sum: 0,
        net_sales_sum: 0,
        net_amount_sum: 0,
      });
    }

    const bucket = buckets.get(placement);
    bucket.total_spend_sum += safeNumber(row.total_spend_sum);
    bucket.total_leads_sum += safeNumber(row.total_leads_sum);
    bucket.booked_sum += safeNumber(row.booked_sum);
    bucket.scheduled_sum += safeNumber(row.scheduled_sum);
    bucket.issues_sum += safeNumber(row.issues_sum);
    bucket.demos_sum += safeNumber(row.demos_sum);
    bucket.gross_sales_sum += safeNumber(row.gross_sales_sum || row.gross_sold_sum);
    bucket.net_sales_sum += safeNumber(row.net_sales_sum || row.net_sold_sum);
    bucket.net_amount_sum += safeNumber(row.net_amount_sum);
  });

  // Biggest spend first; the unattributed bucket always sits at the bottom.
  return Array.from(buckets.values()).sort((a, b) => {
    if (a.placement === UNKNOWN) return 1;
    if (b.placement === UNKNOWN) return -1;
    return b.total_spend_sum - a.total_spend_sum;
  });
}

function buildTotals(rows = []) {
  return rows.reduce(
    (acc, row = {}) => {
      acc.spend += safeNumber(row.total_spend_sum);
      acc.leads += safeNumber(row.total_leads_sum);
      acc.booked += safeNumber(row.booked_sum);
      acc.scheduled += safeNumber(row.scheduled_sum);
      acc.issues += safeNumber(row.issues_sum);
      acc.demos += safeNumber(row.demos_sum);
      acc.grossSold += safeNumber(row.gross_sales_sum);
      acc.netSold += safeNumber(row.net_sales_sum);
      acc.netAmount += safeNumber(row.net_amount_sum);
      if (row.placement !== UNKNOWN) {
        acc.attributedLeads += safeNumber(row.total_leads_sum);
      }
      return acc;
    },
    {
      spend: 0,
      leads: 0,
      booked: 0,
      scheduled: 0,
      issues: 0,
      demos: 0,
      grossSold: 0,
      netSold: 0,
      netAmount: 0,
      attributedLeads: 0,
    }
  );
}

const compactTableSx = {
  '& .MuiTableCell-root': { py: 0.75, px: 1.25, whiteSpace: 'nowrap' },
  '& .MuiTableHead-root .MuiTableCell-root': {
    fontWeight: 700,
    bgcolor: 'background.neutral',
  },
};

const Dash = () => <Typography variant="body2" color="text.disabled">—</Typography>;

function MetricsRow({ row, totals }) {
  const spend = safeNumber(row.total_spend_sum);
  const leads = safeNumber(row.total_leads_sum);
  const booked = safeNumber(row.booked_sum);
  const issues = safeNumber(row.issues_sum);
  const demos = safeNumber(row.demos_sum);
  const netSold = safeNumber(row.net_sales_sum);
  const netAmount = safeNumber(row.net_amount_sum);
  const totalLeads = safeNumber(totals?.leads);
  // Leads whose macro never resolved carry no spend, so a CPL or cost of
  // marketing on that row would be the same fake number the gender table's
  // Unknown row showed. Blank those cells instead of printing $0.
  const spendMeaningful = row.placement !== UNKNOWN && spend > 0;

  return (
    <TableRow sx={row.placement === UNKNOWN ? { '& td': { color: 'text.secondary' } } : undefined}>
      <TableCell sx={{ fontWeight: 600 }}>{row.label}</TableCell>
      <TableCell><PercentageFormatter value={safeDiv(leads, totalLeads)} /></TableCell>
      <TableCell>{spendMeaningful ? <DollarFormatter value={spend} /> : <Dash />}</TableCell>
      <TableCell>{leads}</TableCell>
      <TableCell>{spendMeaningful ? <DollarFormatter value={safeDiv(spend, leads)} /> : <Dash />}</TableCell>
      <TableCell>{booked}</TableCell>
      <TableCell><PercentageFormatter value={safeDiv(booked, leads)} /></TableCell>
      <TableCell>{issues}</TableCell>
      <TableCell>{demos}</TableCell>
      <TableCell>{netSold}</TableCell>
      <TableCell><PercentageFormatter value={safeDiv(netSold, demos)} /></TableCell>
      <TableCell><PercentageFormatter value={safeDiv(netSold, leads)} /></TableCell>
      <TableCell><DollarFormatter value={netAmount} /></TableCell>
      <TableCell><DollarFormatter value={safeDiv(netAmount, netSold)} /></TableCell>
      <TableCell><DollarFormatter value={safeDiv(netAmount, leads)} /></TableCell>
      <TableCell>{spendMeaningful ? <PercentageFormatter value={safeDiv(spend, netAmount)} /> : <Dash />}</TableCell>
    </TableRow>
  );
}

export default function PlacementPerformancePage() {
  const defaultRange = React.useMemo(() => getCurrentMonthRange(), []);
  const [startDate, setStartDate] = React.useState(defaultRange.start);
  const [endDate, setEndDate] = React.useState(defaultRange.end);
  const [selectedClientId, setSelectedClientId] = React.useState('');
  const [selectedVertical, setSelectedVertical] = React.useState('');
  const [rawRows, setRawRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const {
    options: clientOptions,
    loading: clientsLoading,
    handleInputChange: handleClientInputChange,
    findOptionByValue: findClientOptionByValue,
  } = useClientAutocomplete();

  React.useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const payload = {
          date_after: formatDateToYMD(startDate),
          date_before: formatDateToYMD(endDate),
          ...(selectedClientId ? { client_id: selectedClientId } : {}),
          ...(selectedVertical ? { vertical: selectedVertical } : {}),
        };
        const response = await fetchDailyPulseByPlacement(payload);
        if (!active) {
          return;
        }
        setRawRows(Array.isArray(response?.data) ? response.data : []);
      } catch (err) {
        console.error('Failed to load placement performance data', err);
        if (active) {
          setError('Failed to load placement performance data.');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [startDate, endDate, selectedClientId, selectedVertical]);

  const rows = React.useMemo(
    () => aggregatePlacementRows(rawRows, selectedClientId, selectedVertical),
    [rawRows, selectedClientId, selectedVertical]
  );
  const totals = React.useMemo(() => buildTotals(rows), [rows]);
  const coverage = safeDiv(totals.attributedLeads, totals.leads);
  const verticalOptions = React.useMemo(
    () =>
      Array.from(
        new Set(
          rawRows
            .map((row = {}) => (row?.vertical_label || row?.vertical || '').trim())
            .filter(Boolean)
        )
      ).sort((a, b) => a.localeCompare(b)),
    [rawRows]
  );

  const handleDateRangeChange = React.useCallback((ranges) => {
    const nextStart = ranges?.selection?.startDate;
    const nextEnd = ranges?.selection?.endDate;
    if (nextStart instanceof Date && nextEnd instanceof Date) {
      setStartDate(nextStart);
      setEndDate(nextEnd);
    }
  }, []);

  return (
    <Page title="Placement Performance" fullWidth isLoading={loading}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h5">Placement Performance</Typography>
          <Typography variant="body2" color="text.secondary">
            Cost, funnel and revenue per lead split by Facebook placement. Spend comes
            from Facebook&apos;s placement breakdown and leads from the placement macro on
            each lead, both translated to one vocabulary so CPL and cost of marketing
            compare the same people. Facebook only — other platforms have no placement
            breakdown.
          </Typography>
        </Box>

        <Paper sx={{ p: { xs: 1.25, md: 1.5 } }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', md: 'center' }}
          >
            <Box sx={{ minWidth: { xs: '100%', md: 320 } }}>
              <DateRangePicker
                startDate={startDate}
                endDate={endDate}
                onChange={handleDateRangeChange}
                style={{ width: '100%' }}
              />
            </Box>
            <Autocomplete
              sx={{ minWidth: 260 }}
              options={clientOptions}
              loading={clientsLoading}
              value={findClientOptionByValue(selectedClientId)}
              onInputChange={handleClientInputChange}
              onChange={(_event, option) => setSelectedClientId(option ? String(option.value) : '')}
              isOptionEqualToValue={(option, value) => String(option.value) === String(value.value)}
              getOptionLabel={(option) => option?.label || ''}
              renderInput={(params) => (
                <TextField {...params} label="Client" placeholder="All clients" />
              )}
            />
            <Autocomplete
              sx={{ minWidth: 260 }}
              options={verticalOptions}
              value={selectedVertical || null}
              onChange={(_event, option) => setSelectedVertical(option || '')}
              getOptionLabel={(option) => option || ''}
              renderInput={(params) => (
                <TextField {...params} label="Vertical" placeholder="All verticals" />
              )}
            />
          </Stack>
        </Paper>

        {error ? <Alert severity="error">{error}</Alert> : null}

        <Card>
          <CardHeader
            title="Performance by Placement"
            subheader={
              totals.leads
                ? `${Math.round(coverage * 100)}% of leads carry a placement. The rest sit in the Unattributed row with their sales but no spend, so CPL and cost of marketing above are overstated by at most that share.`
                : 'Aggregated across the selected date range, client, and vertical.'
            }
          />
          <CardContent>
            <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={compactTableSx}>
                <TableHead>
                  <TableRow>
                    <TableCell>Placement</TableCell>
                    <TableCell>Lead Mix</TableCell>
                    <TableCell>Spend</TableCell>
                    <TableCell>Leads</TableCell>
                    <TableCell>CPL</TableCell>
                    <TableCell>Booked</TableCell>
                    <TableCell>Set Rate</TableCell>
                    <TableCell>Issues</TableCell>
                    <TableCell>Demos</TableCell>
                    <TableCell>Net Sold</TableCell>
                    <TableCell>Close Rate</TableCell>
                    <TableCell>Lead → Sale</TableCell>
                    <TableCell>Net Amount</TableCell>
                    <TableCell>Avg Deal</TableCell>
                    <TableCell>RPL</TableCell>
                    <TableCell>Cost of Marketing</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length ? (
                    <>
                      {rows.map((row) => (
                        <MetricsRow key={row.placement} row={row} totals={totals} />
                      ))}
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                        <TableCell />
                        <TableCell sx={{ fontWeight: 700 }}>
                          <DollarFormatter value={totals.spend} />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{totals.leads}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <DollarFormatter value={safeDiv(totals.spend, totals.leads)} />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{totals.booked}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <PercentageFormatter value={safeDiv(totals.booked, totals.leads)} />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{totals.issues}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{totals.demos}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{totals.netSold}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <PercentageFormatter value={safeDiv(totals.netSold, totals.demos)} />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <PercentageFormatter value={safeDiv(totals.netSold, totals.leads)} />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <DollarFormatter value={totals.netAmount} />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <DollarFormatter value={safeDiv(totals.netAmount, totals.netSold)} />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <DollarFormatter value={safeDiv(totals.netAmount, totals.leads)} />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <PercentageFormatter value={safeDiv(totals.spend, totals.netAmount)} />
                        </TableCell>
                      </TableRow>
                    </>
                  ) : (
                    <TableRow>
                      <TableCell colSpan={16} align="center">
                        <Typography variant="body2" color="text.secondary">
                          {loading ? 'Loading…' : 'No data for the selected range.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Stack>
    </Page>
  );
}
